// hooks/useLive.ts — hook for the Live page data.
//
// Pure read-only (no store — live data is static seed in prototype phase).
// Components must NOT import livesService directly. Mirrors hooks/useVideos.ts.

import { useState, useEffect } from 'react';
import { livesService } from '@/core/services/livesService';
import type { LiveChannel, LiveCreator } from '@/core/models/live';

export interface LiveHook {
  channels: LiveChannel[];
  creators: LiveCreator[];
  loaded: boolean;
}

export function useLive(): LiveHook {
  const [channels, setChannels] = useState<LiveChannel[]>([]);
  const [creators, setCreators] = useState<LiveCreator[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void Promise.all([livesService.all(), livesService.creators()]).then(([c, cr]) => {
      setChannels(c);
      setCreators(cr);
      setLoaded(true);
    });
  }, []);

  return { channels, creators, loaded };
}

export interface LiveChannelHook {
  channel: LiveChannel | null;
  loaded: boolean;
}

/**
 * A single channel by id, async-resolved on mount / id change. Tracks which id
 * the lookup last settled for (the same technique as useVideo) rather than
 * resetting `loaded` synchronously inside the effect.
 */
export function useLiveChannel(id: string | undefined): LiveChannelHook {
  const [channel, setChannel] = useState<LiveChannel | null>(null);
  const [settledId, setSettledId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (id) {
      void livesService
        .byId(id)
        .then((c) => {
          if (alive) setChannel(c);
        })
        .finally(() => {
          if (alive) setSettledId(id);
        });
    } else {
      const t = setTimeout(() => {
        setChannel(null);
        setSettledId(null);
      }, 0);
      return () => clearTimeout(t);
    }
    return () => {
      alive = false;
    };
  }, [id]);

  return { channel, loaded: !id || settledId === id };
}
